import { AnalyticsBrowser, type AnalyticsBrowserSettings } from '@segment/analytics-next'
import { installAdDestinationGuard, SEGMENT_CDN_URL, SEGMENT_INIT_OPTIONS } from '@quickmd-llc/frontend-utils/segment'
import { captureException } from '@sentry/react'
import { PROVIDER_EVENT_NAMES, type Events, type EventProperties } from './events'
import env from '../../env.json'

const browserSettings: AnalyticsBrowserSettings = {
  cdnURL: SEGMENT_CDN_URL,
  writeKey: env.providerSegmentWriteKey,
}

type IdentifyParams = {
  email?: string
}

class Tracking {
  analytics: AnalyticsBrowser | undefined

  store: any | undefined

  constructor() {
    if (this.analytics) {
      console.warn('Analytics has already loaded')
      return
    }

    const analytics = AnalyticsBrowser.load(browserSettings, SEGMENT_INIT_OPTIONS)

    // Keeps Google Ads from receiving the patient. Registered per client
    // instance, so it has to run against this one. See QMD-4332.
    installAdDestinationGuard(analytics)

    this.analytics = analytics
  }

  identifyUser(id: string, params: IdentifyParams) {
    try {
      this.analytics?.identify(id, params)
    } catch (error) {
      captureException(error)
    }
  }

  /**
   * Returns the underlying reset promise rather than swallowing it, because callers race
   * it against a timeout. A `void` return resolved that race instantly and the timeout
   * bounded nothing — see `utils/clearAllClientState`. The `catch` is attached to the
   * promise as well as wrapping the call: `try`/`catch` alone only sees a synchronous
   * throw, so returning the promise without it would move rejections out of Sentry.
   */
  reset(): Promise<void> {
    try {
      return Promise.resolve(this.analytics?.reset()).then(
        () => undefined,
        (error: unknown) => {
          console.error('Error resetting analytics:', error)
          captureException(error)
        },
      )
    } catch (error) {
      console.error('Error resetting analytics:', error)
      captureException(error)
      return Promise.resolve()
    }
  }

  trackEvent<T extends Events>(event: T, properties?: EventProperties[T]) {
    try {
      this.analytics?.track(event, properties)
    } catch (error) {
      captureException(error)
    }
  }
}

const tracking = new Tracking()

export { PROVIDER_EVENT_NAMES, type Events, tracking }
